import React, { Children, ReactNode, isValidElement, useMemo } from "react";
import type { ReactElement, TableHTMLAttributes } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import UnifiedTable from "@enerex/unified-ui-react/Table";

type TableCell = {
  content: ReactNode;
};

type TableHeader = {
  content: ReactNode;
  onClick?: () => void;
};

type TableRow = {
  cells: TableCell[];
};

type UnifiedMarkupTableProps = Omit<
  TableHTMLAttributes<HTMLTableElement>,
  "children"
> & {
  bordered?: boolean;
  borderless?: boolean;
  hover?: boolean;
  responsive?: boolean | string;
  size?: string;
  striped?: boolean;
  loading?: boolean;
  children?: ReactNode;
  noDataMessage?: ReactNode;
};

const getElementChildren = (node: ReactNode): ReactElement<any>[] =>
  Children.toArray(node).filter(isValidElement) as ReactElement<any>[];

const getSection = (children: ReactNode, sectionName: "thead" | "tbody") =>
  getElementChildren(children).find((child) => child.type === sectionName);

const getRows = (section?: ReactElement<any>) =>
  section ? getElementChildren(section.props.children).filter((child) => child.type === "tr") : [];

const getCells = (row?: ReactElement<any>) =>
  row
    ? getElementChildren(row.props.children).filter(
        (child) => child.type === "th" || child.type === "td"
      )
    : [];

const UnifiedMarkupTable = ({
  children,
  loading = false,
  noDataMessage = "No data found",
}: UnifiedMarkupTableProps) => {
  const { columns, data } = useMemo(() => {
    const head = getSection(children, "thead");
    const body = getSection(children, "tbody");
    const headerRows = getRows(head);
    const bodyRows = getRows(body);
    const headerCells = getCells(headerRows[0]);
    const rowCells = bodyRows.map(getCells);
    const headers: TableHeader[] = headerCells.map((header) => ({
      content: header.props.children,
      onClick: header.props.onClick,
    }));
    const columnCount = Math.max(
      headerCells.length,
      ...rowCells.map((cells) => cells.length),
      1
    );

    const parsedColumns: ColumnDef<TableRow, ReactNode>[] = Array.from(
      { length: columnCount },
      (_, index) => {
        const header = headers[index];

        return {
          id: `column_${index}`,
          header: () =>
            header ? (
              <div
                style={{ width: "100%" }}
                onClick={header.onClick}
                role={header.onClick ? "button" : undefined}
                tabIndex={header.onClick ? 0 : undefined}
                onKeyDown={(event) => {
                  if (
                    header.onClick &&
                    (event.key === "Enter" || event.key === " ")
                  ) {
                    event.preventDefault();
                    header.onClick();
                  }
                }}
              >
                {header.content}
              </div>
            ) : (
              ""
            ),
          cell: ({ row }) => {
            const cell = row.original.cells[index];

            if (!cell) {
              return null;
            }

            return cell.content;
          },
          enableSorting: false,
        };
      }
    );

    const parsedRows = bodyRows.map((row, rowIndex) => ({
      cells: rowCells[rowIndex].map((cell) => ({
        content: cell.props.children,
      })),
    }));

    return { columns: parsedColumns, data: parsedRows };
  }, [children]);

  return (
    <div>
      <UnifiedTable<TableRow>
        columns={columns}
        data={data}
        enableSearch={false}
        enablePagination={false}
        enableHideColumns={false}
        enableSorting={false}
        enableFilters={false}
        enableSelection={false}
        enablePaginateAll={false}
        enableColumnVisibilityStorage={false}
        enableGlobalFilterStorage={false}
        enableUrlPagination={false}
        loading={loading}
        noDataMessage={noDataMessage}
      />
    </div>
  );
};

export default UnifiedMarkupTable;
